import React, { useState } from "react";
import { Plus, Trash2, X, Check } from "lucide-react";
import { Field, Input, Textarea, Select } from "@/components/FormBits";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "checkbox", label: "Yes / No" },
  { value: "dropdown", label: "Dropdown" },
];

function newId() {
  return (crypto?.randomUUID && crypto.randomUUID()) || Math.random().toString(36).slice(2);
}

function CustomFieldEditor({ field, onChange, onRemove }) {
  const { type, value } = field;
  const set = (v) => onChange({ ...field, value: v });

  let input = null;
  if (type === "text") {
    input = <Input value={value || ""} onChange={(e) => set(e.target.value)} />;
  } else if (type === "textarea") {
    input = <Textarea value={value || ""} onChange={(e) => set(e.target.value)} />;
  } else if (type === "number") {
    input = <Input type="number" value={value ?? ""} onChange={(e) => set(e.target.value)} />;
  } else if (type === "date") {
    input = <Input type="date" value={value || ""} onChange={(e) => set(e.target.value)} />;
  } else if (type === "checkbox") {
    input = (
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => set(e.target.checked)}
          className="h-4 w-4 accent-amber-500"
        />
        <span className="text-sm text-gray-700">{value ? "Yes" : "No"}</span>
      </label>
    );
  } else if (type === "dropdown") {
    input = (
      <Select
        options={field.options || []}
        value={value || ""}
        onChange={(e) => set(e.target.value)}
      />
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid={`custom-field-${field.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <Field label={field.label || "(Untitled custom field)"} hint={field.hint}>
            {input}
          </Field>
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove custom field"
          className="text-gray-400 hover:text-red-600 rounded-md p-1.5 -mt-1"
          data-testid={`custom-field-remove-${field.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function CustomFieldsBlock({ project, update, section }) {
  const all = project.custom_fields || [];
  const fields = all.filter((f) => f.section === section);
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [type, setType] = useState("text");
  const [optsRaw, setOptsRaw] = useState("");

  const persist = (next) => update({ custom_fields: next });

  const onChangeField = (id, patch) => {
    persist(all.map((f) => (f.id === id ? patch : f)));
  };
  const onRemoveField = (id) => {
    persist(all.filter((f) => f.id !== id));
  };

  const submitNew = () => {
    const lbl = label.trim();
    if (!lbl) return;
    const newField = {
      id: newId(),
      section,
      label: lbl,
      type,
      value: type === "checkbox" ? false : "",
      options:
        type === "dropdown"
          ? optsRaw.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
      added_at: new Date().toISOString(),
      added_by: project.submitter_name || "",
    };
    persist([...all, newField]);
    setLabel("");
    setOptsRaw("");
    setType("text");
    setAdding(false);
  };

  return (
    <div className="pt-4 mt-6 border-t border-gray-100">
      <div className="rcw-label mb-3">Custom fields for this section</div>

      {fields.length > 0 ? (
        <div className="space-y-3">
          {fields.map((f) => (
            <CustomFieldEditor
              key={f.id}
              field={f}
              onChange={(p) => onChangeField(f.id, p)}
              onRemove={() => onRemoveField(f.id)}
            />
          ))}
        </div>
      ) : null}

      {!adding ? (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="rcw-btn-ghost text-sm mt-3"
          data-testid={`add-custom-field-${section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          <Plus className="h-4 w-4" /> Add custom field
        </button>
      ) : (
        <div className="mt-4 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Field name">
              <Input
                autoFocus
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Site supervisor"
                data-testid={`new-custom-field-label-${section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              />
            </Field>
            <Field label="Field type">
              <select
                className="rcw-input"
                value={type}
                onChange={(e) => setType(e.target.value)}
                data-testid="new-custom-field-type"
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {type === "dropdown" ? (
            <Field label="Dropdown options (comma separated)">
              <Input
                value={optsRaw}
                onChange={(e) => setOptsRaw(e.target.value)}
                placeholder="Option 1, Option 2, Option 3"
              />
            </Field>
          ) : null}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setAdding(false)} className="rcw-btn-ghost text-sm">
              <X className="h-4 w-4" /> Cancel
            </button>
            <button
              type="button"
              onClick={submitNew}
              className="rcw-btn-amber text-sm"
              data-testid={`new-custom-field-confirm-${section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <Check className="h-4 w-4" /> Add field
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
